'use client';

import { useState } from 'react';

import { BlockHeader, BlockHeaderContent } from '@/cms/BlockHeaderContentDC';
import { BlockWrapper } from '@/cms/BlockWrapperDC';
import { TestimonialActions } from '@/blocks/Testimonials/Components/TestimonialsActionArrows';
import { ImageStrapiProps } from '@/components/atoms/Image/ImageStrapi';
import { RichTextFieldStrapiProps } from '@/components/atoms/RichTextField/RichTextFieldStrapi';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';

import BackgroundWrapper from './Components/BackgroundWrapper';
import CompanyCard from './Components/CompanyCard';
import TestimonialCard from './Components/TestimonialCard';

export type Testimonial = {
  id: string;
  name: string;
  companyName: string;
  logo: ImageStrapiProps;
  companySize?: string;
  industry?: string;
  testimonial: {
    header: string;
    description: RichTextFieldStrapiProps;
    authorName: string;
    authorRole: string;
    caseStudyUrl?: string;
  };
};

export type TestimonialsProps = {
  header: BlockHeader;
  testimonials: Testimonial[];
};

const MAX_COMPANY_CARDS_TO_DISPLAY = 5;

export function Testimonials({ header, testimonials }: TestimonialsProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  const activeTestimonial = testimonials[activeIndex];
  const shouldDisplayCompanyCards = testimonials.length > 3;

  const companyCardsToDisplay = shouldDisplayCompanyCards
    ? testimonials.slice(0, MAX_COMPANY_CARDS_TO_DISPLAY)
    : [];

  const handlePrevious = () => {
    setActiveIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  const handleLogoClick = (index: number) => {
    setActiveIndex(index);
  };

  return (
    <div className='overflow-hidden'>
      <BackgroundWrapper>
        <BlockWrapper data-tracker-section={SECTION_NAMES.TESTIMONIALS}>
          <BlockHeaderContent
            {...header}
            alignment='center'
            layoutOptions={{ constrainWidthWithPadding: true }}
          >
            <div className='w-full flex flex-col lg:gap-y-4 gap-y-6 z-20'>
              {testimonials.length > 1 && (
                <TestimonialActions
                  handlePrevious={handlePrevious}
                  handleNext={handleNext}
                  isPreviousDisabled={activeIndex === 0}
                  isNextDisabled={activeIndex === testimonials.length - 1}
                />
              )}

              <TestimonialCard testimonial={activeTestimonial} />

              {shouldDisplayCompanyCards && (
                <div className='hidden w-full lg:flex lg:justify-center mt-4'>
                  <div className='flex w-full gap-3'>
                    {companyCardsToDisplay.map((testimonial, index) => (
                      <CompanyCard
                        key={testimonial.id}
                        testimonial={testimonial}
                        isSelected={index === activeIndex}
                        handleClick={() => handleLogoClick(index)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </BlockHeaderContent>
        </BlockWrapper>
      </BackgroundWrapper>
    </div>
  );
}
